import type { AnyAnalytixEvent } from './event'

import { logger } from '../lib'
import { ShuntingQueue } from './shunting-queue'

/**
 * Async dispatcher for {@link AnalytixEvent} instances.
 *
 * Owns both the {@link ShuntingQueue} and the async wakeup lifecycle. Callers
 * add events via {@link add} and consume them by iterating {@link dispatch}.
 *
 * ## Wakeup sources
 * The dispatcher wakes up in two situations:
 * - {@link add} is called with a new event.
 * - A previously yielded event's `readiness` promise resolves.
 *
 * @example
 * ```ts
 * const dispatcher = new AnalytixDispatcher()
 *
 * dispatcher.add(event)
 *
 * for await (const event of dispatcher.dispatch()) {
 *   await handle(event)
 * }
 * ```
 */
export class AnalytixDispatcher {
  private queue: ShuntingQueue<AnyAnalytixEvent>
  private idle!: Promise<void>
  private wake!: () => void

  constructor() {
    this.queue = new ShuntingQueue()
    this.oyasumi()
  }

  /**
   * Resets the idle promise, putting the dispatcher back to sleep.
   * Called once during construction and again after each wakeup in
   * {@link sleep}.
   *
   * Named after the Japanese おやすみ ("goodnight").
   */
  private oyasumi(): void {
    const { promise, resolve } = Promise.withResolvers<void>()
    this.idle = promise
    this.wake = resolve
  }

  /**
   * Adds an event to the queue, registers a wakeup for when the event becomes
   * ready, and wakes the dispatcher immediately.
   *
   * @param event - The event to dispatch.
   */
  add(event: AnyAnalytixEvent): void {
    this.queue.enqueue(event)
    event.readiness.then((success) => success && this.wake())
    this.wake()
  }

  /**
   * Async generator that yields events from the queue as they become ready.
   *
   * Sleeps without busy-waiting when the queue has nothing ready, and resumes
   * automatically when a new event is added or an existing event becomes ready.
   * Runs indefinitely until the caller breaks out of the loop or calls
   * `.return()` on the generator.
   *
   * @yields The next ready {@link AnalytixEvent}.
   *
   * @example
   * ```ts
   * for await (const event of dispatcher.dispatch()) {
   *   await handle(event)
   * }
   * ```
   */
  async *dispatch(): AsyncGenerator<AnyAnalytixEvent> {
    logger.info('dispatcher started')
    try {
      while (true) {
        const event = this.queue.dequeue()

        if (event) {
          yield event
          continue
        }

        await this.sleep()
      }
    } finally {
      logger.info('dispatcher stopped')
    }
  }

  /**
   * Suspends the dispatch loop until {@link wake} is called, then resets the
   * idle promise via {@link oyasumi} so the dispatcher can sleep again on the
   * next cycle.
   *
   * If {@link wake} was already called before this method is awaited, the
   * promise is already resolved and this returns immediately.
   */
  private async sleep(): Promise<void> {
    await this.idle
    this.oyasumi()
  }
}
